import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/useAuthStore';
import { tasksKeys } from './queries/useTasks';
import { taskCollabKeys } from './queries/useTaskCollaboration';

/**
 * Realtime sync sur les tâches et leurs satellites collaboratifs.
 * Invalide React Query au lieu de patcher manuellement le cache (plus simple,
 * suffisant pour le volume actuel d'un cabinet).
 */
export function useTasksRealtime() {
  const queryClient = useQueryClient();
  const currentCollaboratorId = useAuthStore((s) => s.collaborator?.id);

  useEffect(() => {
    if (!currentCollaboratorId) return;

    const channel = supabase
      .channel('tasks-changes')
      // ── tasks ───────────────────────────────────────────────
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        () => {
          void queryClient.invalidateQueries({ queryKey: tasksKeys.all });
          void queryClient.invalidateQueries({ queryKey: ['tasks', 'archived'] });
        },
      )
      // ── task_sections ───────────────────────────────────────
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_sections' },
        () => {
          void queryClient.invalidateQueries({ queryKey: ['tasks', 'sections'] });
        },
      )
      // ── task_assignments ────────────────────────────────────
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_assignments' },
        () => {
          void queryClient.invalidateQueries({ queryKey: ['tasks', 'assignees'] });
          void queryClient.invalidateQueries({ queryKey: ['tasks', 'assignments'] });
        },
      )
      // ── task_comments ───────────────────────────────────────
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_comments' },
        (payload) => {
          const taskId =
            (payload.new as { task_id?: string } | null)?.task_id ??
            (payload.old as { task_id?: string } | null)?.task_id;
          if (taskId) {
            void queryClient.invalidateQueries({
              queryKey: taskCollabKeys.comments(taskId),
            });
          }
          void queryClient.invalidateQueries({ queryKey: ['tasks', 'commentCounts'] });
        },
      )
      // ── task_attachments ────────────────────────────────────
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_attachments' },
        (payload) => {
          const taskId =
            (payload.new as { task_id?: string } | null)?.task_id ??
            (payload.old as { task_id?: string } | null)?.task_id;
          if (taskId) {
            void queryClient.invalidateQueries({
              queryKey: taskCollabKeys.attachments(taskId),
            });
          }
          void queryClient.invalidateQueries({ queryKey: ['tasks', 'attachmentCounts'] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [currentCollaboratorId, queryClient]);
}
